/**
 * What a notification row does on web, shared by the full Notifications page
 * and the My Tabbio Notifications pop-out: open (mark read optimistically,
 * keep the unread badge in step, go to the target), prefetch a job on intent,
 * delete, and mark all read.
 *
 * `decorateTarget` lets each surface shape where a row goes: the page adds
 * its list origin (so Back from a job returns to the list), the pop-out adds
 * the hub return instead. `onNavigate` runs right before a navigation (the
 * pop-out closes itself); a row with no target only marks itself read.
 */
import { useCallback } from "react";
import { useRouter } from "expo-router";
import { useQueryClient } from "@tanstack/react-query";
import type { NotificationSummary } from "@tabbio/types";
import {
  deleteNotification,
  markAllNotificationsRead,
  markNotificationRead,
} from "../../lib/notifications-api";
import { QUERY_KEYS } from "../../lib/query-keys";
import {
  setNotificationUnreadCount,
  updateNotificationUnreadCount,
} from "../../lib/notification-count";
import { prefetchNotificationJobDetail } from "../../lib/notification-job-prefetch";

export interface NotificationWebList {
  notifications: NotificationSummary[];
  updateNotification: (id: string, updates: Partial<NotificationSummary>) => void;
  removeNotification: (id: string) => void;
  refetch: () => unknown;
}

export interface NotificationWebActionOptions {
  /** Turns a notification's payload into the route it opens (or null). */
  resolveTarget: (data: unknown) => string | null;
  /** Runs right before navigating to a target. */
  onNavigate?: () => void;
  /**
   * The badge's server count. Unread items can sit on pages the list has not
   * loaded (the pop-out shows only the first page), so "Mark all as read"
   * must also be offered, and must run, when only the server knows of them.
   */
  serverUnreadCount?: number;
}

export function useNotificationWebActions(
  { notifications, updateNotification, removeNotification, refetch }: NotificationWebList,
  { resolveTarget, onNavigate, serverUnreadCount }: NotificationWebActionOptions,
) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const unreadCount = notifications.filter((item) => !item.read).length;
  const hasUnread = unreadCount > 0 || (serverUnreadCount ?? 0) > 0;

  const recountFromServer = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.NOTIFICATION_UNREAD_COUNT,
    });
  }, [queryClient]);

  const handlePress = useCallback(
    (item: NotificationSummary) => {
      void prefetchNotificationJobDetail(queryClient, item.data);
      if (!item.read) {
        updateNotification(item.id, {
          read: true,
          readAt: new Date().toISOString(),
        });
        updateNotificationUnreadCount(queryClient, (current) => current - 1);
        markNotificationRead(item.id).catch(() => {
          updateNotification(item.id, { read: false, readAt: null });
          recountFromServer();
        });
      }
      const target = resolveTarget(item.data);
      if (target) {
        onNavigate?.();
        router.push(target as never);
      }
    },
    [onNavigate, queryClient, recountFromServer, resolveTarget, router, updateNotification],
  );

  const handlePrefetch = useCallback(
    (item: NotificationSummary) => {
      void prefetchNotificationJobDetail(queryClient, item.data);
    },
    [queryClient],
  );

  const handleDelete = useCallback(
    (item: NotificationSummary) => {
      removeNotification(item.id);
      if (!item.read) {
        updateNotificationUnreadCount(queryClient, (current) => current - 1);
      }
      deleteNotification(item.id).catch(() => {
        void refetch();
        recountFromServer();
      });
    },
    [queryClient, recountFromServer, refetch, removeNotification],
  );

  const handleMarkAllRead = useCallback(async () => {
    if (!hasUnread) return;
    const readAt = new Date().toISOString();
    notifications.forEach((item) => {
      if (!item.read) updateNotification(item.id, { read: true, readAt });
    });
    updateNotificationUnreadCount(queryClient, (current) => current - unreadCount);
    try {
      await markAllNotificationsRead();
      // Everything is read now, including pages not loaded yet: subtracting
      // only the loaded unread rows left the badge stuck above zero.
      setNotificationUnreadCount(queryClient, 0);
    } catch {
      void refetch();
      recountFromServer();
    }
  }, [hasUnread, notifications, queryClient, recountFromServer, refetch, unreadCount, updateNotification]);

  return {
    unreadCount,
    hasUnread,
    handlePress,
    handlePrefetch,
    handleDelete,
    handleMarkAllRead,
  };
}
